import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Tabs, TabsList, TabsTrigger } from '../components/Tabs';
import { Button } from '../components/Button';
import { Filter, Eye, FileText, Inbox, BarChart2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../data/store';
import { UrgencyBadge } from '../components/UrgencyBadge';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { FilterBar, FilterField } from '../components/FilterBar';
import { Role } from '../data/rbac';
import { BINDA_LIST } from '../data/binda';
export default function Reports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const role = localStorage.getItem('bimcs_role') as Role || 'Super Admin Nasional';
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
    setActiveTab('all');
  };
  const reportsList = useStore(s => s.reports);
  const filteredReports = reportsList.filter(report => {
    const search = filterValues.search?.toLowerCase() || '';
    const matchesSearch = !search || report.title.toLowerCase().includes(search) || report.id.toLowerCase().includes(search);
    const matchesTab = activeTab === 'all' ? true : activeTab === 'pending' ? report.status === 'Belum Diverifikasi' || report.status === 'Review Koordinator' : report.urgency === 'Kritis';
    const matchesRegion = !filterValues.region || filterValues.region === 'all' || report.region === filterValues.region;
    const matchesCategory = !filterValues.category || filterValues.category === 'all' || report.category === filterValues.category;
    const matchesUrgency = !filterValues.urgency || filterValues.urgency === 'all' || report.urgency === filterValues.urgency;
    const matchesStatus = !filterValues.status || filterValues.status === 'all' || report.status === filterValues.status;
    const matchesDate = !filterValues.date || filterValues.date === 'all' || report.time.includes(filterValues.date);
    return matchesSearch && matchesTab && matchesRegion && matchesCategory && matchesUrgency && matchesStatus && matchesDate;
  });
  const filterFields: FilterField[] = [{
    key: 'search',
    label: 'Pencarian',
    type: 'search',
    placeholder: 'Cari ID, judul...'
  }, {
    key: 'date',
    label: 'Tanggal Kejadian',
    type: 'datePreset'
  }, {
    key: 'region',
    label: 'Wilayah',
    type: 'select',
    options: BINDA_LIST.map(b => ({
      label: b.provinsi,
      value: b.provinsi
    }))
  }, {
    key: 'category',
    label: 'Kategori',
    type: 'select',
    options: ['Politik', 'Sosial', 'Ekonomi', 'Keamanan', 'Siber', 'Konflik Sosial', 'Infrastruktur Strategis', 'Objek Vital Nasional', 'Bencana', 'Lintas Negara'].map(c => ({
      label: c,
      value: c
    }))
  }, {
    key: 'urgency',
    label: 'Urgensi',
    type: 'urgencyBadge',
    options: [{
      label: 'Rendah',
      value: 'Rendah'
    }, {
      label: 'Sedang',
      value: 'Sedang'
    }, {
      label: 'Tinggi',
      value: 'Tinggi'
    }, {
      label: 'Kritis',
      value: 'Kritis'
    }]
  }, {
    key: 'status',
    label: 'Status',
    type: 'statusBadge',
    options: ['Belum Diverifikasi', 'Review Koordinator', 'Diverifikasi', 'Dianalisis', 'Disetujui BINDA', 'Perlu Revisi', 'Diterima Nasional', 'Ditindaklanjuti', 'Selesai', 'Ditolak'].map(s => ({
      label: s,
      value: s
    }))
  }];
  const exportColumns = [{
    key: 'id',
    label: 'ID'
  }, {
    key: 'title',
    label: 'Judul'
  }, {
    key: 'category',
    label: 'Kategori'
  }, {
    key: 'urgency',
    label: 'Urgensi'
  }, {
    key: 'region',
    label: 'Wilayah'
  }, {
    key: 'status',
    label: 'Status'
  }];
  return <div className="space-y-6 animate-in fade-in duration-500 max-w-7xl mx-auto pb-12" data-id="element-3750">
      <div className="flex justify-between items-end border-b border-border/50 pb-4" data-id="element-3751">
        <div data-id="element-3752">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground" data-id="element-3753">
            LAPORAN INTELIJEN
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-3754">
            DENS CAKRA // Basis Data Operasional
          </p>
        </div>
        <div className="flex gap-3" data-id="element-3755">
          <Button variant="outline" onClick={() => navigate('/reports/intelligence-summary')} className="font-mono text-xs tracking-widest uppercase border-primary/50 text-primary hover:bg-primary/10" data-id="element-3756">
            <BarChart2 className="w-4 h-4 mr-2" data-id="element-3757" />
            Executive Summary
          </Button>
          {(role === 'Personel Lapangan' || role === 'Admin BINDA' || role === 'Super Admin Nasional') && <Button onClick={() => navigate('/reports/new')} className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs tracking-widest uppercase" data-id="element-3758">
              <FileText className="w-4 h-4 mr-2" data-id="element-3759" />
              Entri Baru
            </Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6" data-id="element-3760">
        <Card className="glass-panel border-border/50 transition-all duration-300" data-id="element-3761">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50" data-id="element-3762">
            <div className="flex justify-between items-center" data-id="element-3763">
              <CardTitle className="text-sm font-mono uppercase tracking-widest" data-id="element-3764">
                Kueri Basis Data
              </CardTitle>
              <div className="flex gap-2" data-id="element-3765">
                <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-3766">
                  <Filter className="w-4 h-4 mr-2" data-id="element-3767" />
                  Filter
                </Button>
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-4" data-id="element-3768">
              <TabsList className="bg-background/50 border border-border/50" data-id="element-3769">
                <TabsTrigger value="all" className="text-xs font-mono uppercase tracking-wider" data-id="element-3770">
                  Semua Rekaman
                </TabsTrigger>
                <TabsTrigger value="pending" className="text-xs font-mono uppercase tracking-wider" data-id="element-3771">
                  Tertunda
                </TabsTrigger>
                <TabsTrigger value="critical" className="text-xs font-mono uppercase tracking-wider text-destructive data-[state=active]:text-destructive" data-id="element-3772">
                  Kritis
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {showFilters && <div className="mt-4" data-id="element-3773">
                <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} resultCount={filteredReports.length} exportData={filteredReports} exportColumns={exportColumns} exportFilename="Laporan_Intelijen_DENS_CAKRA" data-id="element-3774" />
              </div>}
          </CardHeader>
          <CardContent className="p-0" data-id="element-3775">
            {filteredReports.length === 0 ? <EmptyState icon={Inbox} title="TIDAK ADA LAPORAN" description="Tidak ada laporan intelijen yang sesuai dengan kriteria pencarian atau filter saat ini. Silakan sesuaikan parameter kueri Anda." actionLabel="Bersihkan Filter" onAction={handleResetFilters} className="border-0 rounded-none rounded-b-lg" data-id="element-3776" /> : <Table data-id="element-3777">
                <TableHeader className="bg-black/20" data-id="element-3778">
                  <TableRow className="border-border/30 hover:bg-transparent" data-id="element-3779">
                    <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3780">
                      ID
                    </TableHead>
                    <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3781">
                      Judul
                    </TableHead>
                    <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3782">
                      Kategori
                    </TableHead>
                    <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3783">
                      Urgensi
                    </TableHead>
                    <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3784">
                      Wilayah
                    </TableHead>
                    <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3785">
                      Status
                    </TableHead>
                    <TableHead className="text-right text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3786">
                      Aksi
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="element-3787">
                  {filteredReports.map(report => <TableRow key={report.id} className="border-border/20 hover:bg-white/5 transition-colors cursor-pointer border-l-2 border-l-transparent" onClick={() => navigate(`/reports/${report.id}`)} data-id="element-3788">
                      <TableCell className="font-mono text-xs text-muted-foreground" data-id="element-3789">
                        {report.id}
                      </TableCell>
                      <TableCell className="font-medium text-sm" data-id="element-3790">
                        {report.title}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground" data-id="element-3791">
                        {report.category}
                      </TableCell>
                      <TableCell data-id="element-3792">
                        <UrgencyBadge urgency={report.urgency as any} data-id="element-3793" />
                      </TableCell>
                      <TableCell className="text-xs font-mono" data-id="element-3794">
                        {report.region}
                      </TableCell>
                      <TableCell data-id="element-3795">
                        <StatusBadge status={report.status} data-id="element-3796" />
                      </TableCell>
                      <TableCell className="text-right" data-id="element-3797">
                        <Button variant="ghost" size="icon" onClick={e => {
                    e.stopPropagation();
                    navigate(`/reports/${report.id}`);
                  }} className="h-8 w-8 hover:bg-primary/20 hover:text-primary" data-id="element-3798">
                          <Eye className="w-4 h-4" data-id="element-3799" />
                        </Button>
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>}
          </CardContent>
        </Card>
      </div>
    </div>;
}